// Inline-SVG icon set used by the shell + dashboards. Lucide-style
// strokes (1.75px round), inherits currentColor, sized via the `size`
// prop. Kept inline (no new dependency) per the OSF dependency policy.

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & {
  size?: number;
};

function svgProps(size: number, rest: SVGProps<SVGSVGElement>): SVGProps<SVGSVGElement> {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    ...rest,
  };
}

export function IconDashboard({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  );
}

// Radar / broadcast — classic dispatch symbol. Two nested pairs of
// signal arcs radiating from a center dot. Unique to the Dispatch nav
// item so it doesn't collide with IconDashboard's grid.
export function IconRadar({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <circle cx="12" cy="12" r="1.5" />
      <path d="M8.5 8.5a5 5 0 0 0 0 7" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M5.5 5.5a9 9 0 0 0 0 13" />
      <path d="M18.5 5.5a9 9 0 0 1 0 13" />
    </svg>
  );
}

export function IconRoute({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="5" r="2" />
      <path d="M8 19h6a4 4 0 0 0 0-8h-4a4 4 0 0 1 0-8h6" />
    </svg>
  );
}

export function IconTruck({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <path d="M3 7h11v8H3z" />
      <path d="M14 10h4l3 3v2h-7" />
      <circle cx="7" cy="17.5" r="1.75" />
      <circle cx="17" cy="17.5" r="1.75" />
    </svg>
  );
}

export function IconPackage({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <path d="M12 3 3 7.5v9L12 21l9-4.5v-9z" />
      <path d="M3 7.5 12 12l9-4.5" />
      <path d="M12 12v9" />
      <path d="M7.5 5.25 16.5 9.75" />
    </svg>
  );
}

export function IconUsers({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <circle cx="9" cy="8" r="3.25" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M15 14.5c2.8 0 5 2 5 5" />
    </svg>
  );
}

export function IconClipboard({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <rect x="6" y="4" width="12" height="17" rx="2" />
      <rect x="9" y="2.5" width="6" height="4" rx="1" />
      <path d="M9 11h6" />
      <path d="M9 14.5h6" />
      <path d="M9 18h4" />
    </svg>
  );
}

export function IconUpload({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <path d="M12 16V4" />
      <path d="m7 9 5-5 5 5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

export function IconBuilding({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <rect x="4" y="3" width="11" height="18" rx="1.5" />
      <path d="M15 8h4a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-4" />
      <path d="M8 7h3M8 11h3M8 15h3" />
    </svg>
  );
}

export function IconChevronLeft({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}

export function IconChevronRight({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

export function IconTrendUp({ size = 14, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <path d="m3 17 7-7 4 4 7-7" />
      <path d="M14 7h7v7" />
    </svg>
  );
}

export function IconTrendDown({ size = 14, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <path d="m3 7 7 7 4-4 7 7" />
      <path d="M14 17h7v-7" />
    </svg>
  );
}

export function IconDownload({ size = 16, ...rest }: IconProps): JSX.Element {
  // Cloud-free download glyph — an arrow into a tray. Kept simple so
  // it reads at 13px (sidebar) as well as 24px (page-level buttons).
  return (
    <svg {...svgProps(size, rest)}>
      <path d="M12 3v11" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 19h14" />
    </svg>
  );
}

export function IconAlert({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <path d="M12 3 2 20h20Z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="17" r="0.75" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconCheckCircle({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8 12 3 3 5-6" />
    </svg>
  );
}

export function IconClock({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

export function IconCalendar({ size = 16, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M8 3v4M16 3v4M3.5 10h17" />
    </svg>
  );
}

export function IconPill({ size = 18, ...rest }: IconProps): JSX.Element {
  return (
    <svg {...svgProps(size, rest)}>
      <rect
        x="3.5"
        y="9"
        width="17"
        height="6"
        rx="3"
        transform="rotate(-45 12 12)"
      />
      <path d="M9 9 15 15" />
    </svg>
  );
}
